import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, ApiError, type JobDetailResponse } from '../../api/client'
import { StatusBadge } from '../../components/StatusBadge'
import type { JobEvent, JobStatus } from '@agent/shared'

// ISSUE #10: Job 詳細 + イベントタイムライン。terminal status (completed/failed/cancelled)
// になったら polling を止める (refetchInterval を false に切り替え)。
const TERMINAL_STATUSES: ReadonlyArray<JobStatus> = [
  'completed',
  'failed',
  'cancelled',
]

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>()

  const query = useQuery<JobDetailResponse, unknown>({
    queryKey: ['job', id],
    queryFn: () => api.getJob(id!),
    enabled: Boolean(id),
    refetchInterval: (q) => {
      const data = q.state.data
      if (!data) return 2000
      return TERMINAL_STATUSES.includes(data.status) ? false : 2000
    },
  })

  if (!id) {
    return <p className="text-rose-600">Missing job id in URL.</p>
  }

  if (query.isPending) {
    return <p className="text-slate-500">Loading job…</p>
  }
  if (query.isError) {
    const e = query.error
    const msg =
      e instanceof ApiError ? `${e.code}: ${e.message}` : e instanceof Error ? e.message : 'Unknown'
    return (
      <div className="rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
        Failed to load job — {msg}
        <div className="mt-2">
          <Link to="/jobs" className="text-blue-700 underline">
            Back to list
          </Link>
        </div>
      </div>
    )
  }

  const { events, ...job } = query.data
  const isTerminal = TERMINAL_STATUSES.includes(job.status)

  return (
    <section className="space-y-6">
      <div>
        <Link to="/jobs" className="text-sm text-blue-700 hover:underline">
          ← Back to jobs
        </Link>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">
              {job.type}{' '}
              <span className="text-slate-400">— {job.project ?? '(no project)'}</span>
            </h1>
            <p className="mt-1 font-mono text-xs text-slate-500">{job.id}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={job.status} />
            <span className="text-xs text-slate-500">risk: {job.risk_level}</span>
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
          <Field label="Created at" value={new Date(job.created_at).toLocaleString()} />
          <Field label="Updated at" value={new Date(job.updated_at).toLocaleString()} />
          <Field label="Assigned executor" value={job.assigned_executor ?? '—'} />
          <Field label="Artifact R2 prefix" value={job.artifact_r2_prefix ?? '—'} mono />
        </dl>

        <details className="mt-4">
          <summary className="cursor-pointer text-sm text-slate-600">Input</summary>
          <pre className="mt-2 overflow-x-auto rounded bg-slate-900 p-3 text-xs text-slate-100">
            {JSON.stringify(job.input, null, 2)}
          </pre>
        </details>
        {job.result != null && (
          <details className="mt-4" open={isTerminal}>
            <summary className="cursor-pointer text-sm text-slate-600">Result</summary>
            <pre className="mt-2 overflow-x-auto rounded bg-slate-900 p-3 text-xs text-slate-100">
              {JSON.stringify(job.result, null, 2)}
            </pre>
          </details>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Events ({events.length})</h2>
          <span className="text-xs text-slate-500">
            {isTerminal ? 'stopped polling (terminal)' : query.isFetching ? 'refreshing…' : 'polling every 2s'}
          </span>
        </div>
        {events.length === 0 ? (
          <p className="text-sm text-slate-500">No events yet.</p>
        ) : (
          <ol className="relative space-y-3 border-l border-slate-200 pl-4">
            {events.map((ev) => (
              <EventItem key={ev.id} event={ev} />
            ))}
          </ol>
        )}
      </div>
    </section>
  )
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className={`mt-0.5 text-slate-800 ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  )
}

const EVENT_TYPE_COLOR: Record<JobEvent['event_type'], string> = {
  stdout: 'bg-slate-100 text-slate-700',
  stderr: 'bg-amber-100 text-amber-800',
  progress: 'bg-sky-100 text-sky-700',
  info: 'bg-slate-100 text-slate-700',
  warn: 'bg-amber-100 text-amber-800',
  error: 'bg-rose-100 text-rose-800',
  claim: 'bg-blue-100 text-blue-800',
  complete: 'bg-emerald-100 text-emerald-800',
  fail: 'bg-rose-100 text-rose-800',
}

function EventItem({ event }: { event: JobEvent }) {
  const color = EVENT_TYPE_COLOR[event.event_type] ?? 'bg-slate-100 text-slate-700'
  return (
    <li className="relative">
      <span className="absolute -left-[1.4rem] top-1.5 h-2 w-2 rounded-full bg-slate-300" />
      <div className="flex flex-wrap items-baseline gap-2">
        <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${color}`}>
          {event.event_type}
        </span>
        <span className="text-xs text-slate-500">{event.actor}</span>
        <span className="text-xs text-slate-400">{new Date(event.created_at).toLocaleTimeString()}</span>
      </div>
      {Object.keys(event.payload).length > 0 && (
        <pre className="mt-1 overflow-x-auto rounded bg-slate-900 p-2 text-xs text-slate-100">
          {JSON.stringify(event.payload, null, 2)}
        </pre>
      )}
    </li>
  )
}
